import React, { useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import { BulkQuantityEditorProps, BulkQuantityField, removeCt } from "./Utils";
import styled from "styled-components/native";
import { Input, Spacer, VDBText } from "@components/atoms";
import { removeCommaFormatted } from "@components/atoms/Filters/FilterSection/Utils";
import { useTranslation } from "react-i18next";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTheme } from "styled-components/native";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useUserCurrency } from "../../../hooks/getCurrency";
import currency from "currency.js";
import { View } from "react-native";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { formatNumberWithPrecision } from "@utils/product/productFormatters";
import { DefaultTheme } from "styled-components/native";
import { useAppSettings } from "@redux/slices/globalSlice";

const BulkQuantityEditor = ({
  mItem,
  priceDetailsDataOptions,
  onBulkEditorChange,
  viewOnly,
}: BulkQuantityEditorProps) => {
  const getAppSettings = useAppSettings();
  const enableFourDecimalCarat =
    getAppSettings?.enable_four_decimal_carat ?? false;
  const MAX_QUANTITY = 9999;
  const MAX_TOTAL_CARAT = formatNumberWithPrecision(
    Number(mItem?.size) * Number(MAX_QUANTITY),
    { precision: enableFourDecimalCarat ? 4 : 3 }
  );
  const mComments = (mItem as any)?.updated_comments
    ? (mItem as any)?.updated_comments
    : mItem?.comments
    ? mItem?.comments
    : "";
  const { t } = useTranslation();
  const theme = useTheme();
  const typography = useTypographyStore();
  const userCurrency = useUserCurrency();
  const [quantity, setQuantity] = useState(
    mItem?.quantity && viewOnly ? mItem?.quantity?.toString() : ""
  );
  const [qtyError, setQtyError] = useState("");
  const [totalCarat, setTotalCarat] = useState(
    mItem?.offer_size ? mItem?.offer_size + " ct" : ""
  );
  const [caratError, setCaratError] = useState("");
  const [comments, setComments] = useState(mComments);

  const propagateChange = (
    mQuantity: string,
    mTotalCarat: string,
    _comments: string
  ) => {
    onBulkEditorChange(
      mQuantity,
      removeCt(mTotalCarat),
      estimatedTotalPrice(mTotalCarat),
      _comments
    );
  };

  const propagateRef = React.useRef(propagateChange);
  propagateRef.current = propagateChange;
  const latestValuesRef = React.useRef({ quantity, totalCarat, comments });
  latestValuesRef.current = { quantity, totalCarat, comments };

  const debouncedPropagate = useMemo(
    () =>
      debounce(
        (mQuantity: string, mTotalCarat: string, xComments: string) =>
          propagateRef.current(mQuantity, mTotalCarat, xComments),
        400
      ),
    []
  );

  useEffect(() => () => debouncedPropagate.cancel(), [debouncedPropagate]);

  const onBlurUpdate = () => {
    debouncedPropagate.cancel();
    propagateChange(quantity, totalCarat, comments);
  };

  const onValueChange = useCallback(
    (fieldName: string, val: string) => {
      const lastChar = val.slice(-1);
      const isDecimalAttempt = lastChar === ",";

      const formatDecimalValue = (
        value: string,
        mIsDecimalAttempt: boolean
      ) => {
        let rawValue = value.replace(/,/g, "");
        let hasUnit = rawValue.toLowerCase().endsWith(" ct");

        if (hasUnit) {
          rawValue = rawValue.slice(0, -3);
        }

        // now only clean numeric part
        let formattedValue = rawValue.replace(/[^0-9.]/g, "");
        const firstDecimalIndex = formattedValue.indexOf(".");

        if (firstDecimalIndex !== -1) {
          formattedValue =
            formattedValue.slice(0, firstDecimalIndex + 1) +
            formattedValue.slice(firstDecimalIndex + 1).replace(/\./g, "");
          formattedValue = formattedValue.slice(
            0,
            firstDecimalIndex + (enableFourDecimalCarat ? 5 : 4)
          );
        }

        if (formattedValue.startsWith(".")) {
          formattedValue = "0" + formattedValue;
        }

        if (mIsDecimalAttempt && !formattedValue.includes(".")) {
          formattedValue += ".";
        }

        // ✅ append unit back if it was there
        if (hasUnit) {
          formattedValue += " ct";
        }

        return formattedValue;
      };

      if (fieldName === BulkQuantityField.Quantity) {
        const formattedValue = val.replace(/,/g, "").replace(/[^0-9]/g, "");
        setQtyError("");
        setCaratError("");
        if (Number(formattedValue) > MAX_QUANTITY) {
          setQtyError(`Maximum quantity is ${MAX_QUANTITY}`);
          return;
        }
        setQuantity(formattedValue);
        let nextTotalCarat = "";
        if (
          formattedValue === "" ||
          formattedValue === "0" ||
          formattedValue === undefined
        ) {
          setTotalCarat("");
        } else {
          const totalCaratVal = formatNumberWithPrecision(
            Number(formattedValue) * Number(mItem?.size),
            { precision: enableFourDecimalCarat ? 4 : 3 }
          );
          nextTotalCarat = totalCaratVal + " ct";
          setTotalCarat(nextTotalCarat);
        }
        setQtyError("");
        debouncedPropagate(
          formattedValue,
          nextTotalCarat,
          latestValuesRef.current.comments
        );
      } else if (fieldName === BulkQuantityField.TotalCarat) {
        setQtyError("");
        setCaratError("");
        if (Number(removeCommaFormatted(val)) > Number(MAX_TOTAL_CARAT)) {
          setCaratError(`Maximum total carat is ${MAX_TOTAL_CARAT}`);
          return;
        }
        const formattedValue = formatDecimalValue(val, isDecimalAttempt);

        setTotalCarat(formattedValue);

        let nextQuantity = "";
        if (
          formattedValue === "" ||
          formattedValue === "0" ||
          formattedValue === undefined
        ) {
          setQuantity("");
        } else {
          const quantityVal = formatNumberWithPrecision(
            Number(removeCt(formattedValue)) / Number(mItem?.size),
            { precision: 0 }
          );
          nextQuantity = quantityVal ?? "";
          setQuantity(nextQuantity);
        }
        setCaratError("");
        debouncedPropagate(
          nextQuantity,
          formattedValue,
          latestValuesRef.current.comments
        );
      } else {
        setComments(val);
        debouncedPropagate(
          latestValuesRef.current.quantity,
          latestValuesRef.current.totalCarat,
          val
        );
      }
    },
    [
      mItem?.size,
      setQuantity,
      setTotalCarat,
      setQtyError,
      setCaratError,
      setComments,
      debouncedPropagate,
    ]
  );

  const getLabel = (labelKey: string, defaultValue: string) => {
    const element = priceDetailsDataOptions?.find(
      (el) => el?.name === labelKey
    );
    return element ? t(element.label) : defaultValue;
  };

  const getEstimatedPrice = useMemo(() => {
    const estPrice = formatNumberWithPrecision(
      Number(removeCt(totalCarat)) * Number(mItem?.price_per_carat),
      { precision: 2 }
    );

    if (estPrice === "0.00" || estPrice === "NaN" || estPrice === null) {
      return "-";
    }

    return (
      BulkQuantityField.APPROX_EQUAL +
      currency(
        viewOnly ? mItem?.updated_total_sales_price || estPrice : estPrice,
        {
          symbol: userCurrency,
          precision: 2,
        }
      ).format()
    );
  }, [totalCarat, mItem?.price_per_carat, userCurrency, viewOnly]);

  const estimatedTotalPrice = (mTotalCarat: string) => {
    const estimatedPrice = formatNumberWithPrecision(
      Number(removeCt(mTotalCarat)) * Number(mItem?.price_per_carat),
      { precision: 2 }
    );
    return estimatedPrice ?? "";
  };

  const { deviceWidth: screenWidth } = useIsFoldable();
  const containerWidth = screenWidth - 96;

  return (
    <ParentContainer>
      {viewOnly
        ? RenderStaticForm(
            getLabel,
            quantity,
            totalCarat,
            typography,
            getEstimatedPrice,
            theme
          )
        : RenderEditableForm(
            getLabel,
            quantity,
            comments,
            totalCarat,
            onValueChange,
            onBlurUpdate,
            typography,
            getEstimatedPrice,
            qtyError,
            caratError,
            theme,
            t,
            containerWidth,
            enableFourDecimalCarat
          )}
    </ParentContainer>
  );
};

const RenderEditableForm = (
  getLabel: any,
  quantity: string,
  comments: string,
  totalCarat: string,
  onValueChange: any,
  onBlurUpdate: any,
  typography: any,
  getEstimatedPrice: any,
  qtyError: any,
  caratError: any,
  theme: any,
  t: any,
  containerWidth: Number,
  enableFourDecimalCarat: boolean
) => {
  return (
    <>
      <DataContainer>
        <QuantityInput
          label={getLabel("carat", "Total Carat")}
          fieldName={BulkQuantityField.TotalCarat}
          quantity={quantity}
          totalCarat={totalCarat}
          onValueChange={onValueChange}
          onBlurChange={onBlurUpdate}
          containerWidth={containerWidth}
          enableFourDecimalCarat={enableFourDecimalCarat}
        />
        <Spacer size={SpaceType.Double} />
        <QuantityInput
          label={getLabel("quantity", "Quantity")}
          fieldName={BulkQuantityField.Quantity}
          quantity={quantity}
          totalCarat={totalCarat}
          onValueChange={onValueChange}
          onBlurChange={onBlurUpdate}
          containerWidth={containerWidth}
          enableFourDecimalCarat={enableFourDecimalCarat}
        />
        <Spacer size={SpaceType.Double} />
        <EstimatedPriceContainer
          containerWidth={
            theme.isLargeScreen || theme.isTablet
              ? 100
              : (containerWidth as number) / 3
          }
        >
          <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
            {getLabel("price", "* Est. Price")}
          </VDBText>
          <Spacer size={SpaceType.Double} />
          <PriceContainer>
            <VDBText type={typography["Minor-M"]} color={theme["core-05"]}>
              {getEstimatedPrice}
            </VDBText>
          </PriceContainer>
        </EstimatedPriceContainer>
      </DataContainer>
      {qtyError || caratError ? (
        <ErrorContainer>
          <VDBText type={typography["Minor-R"]} color={theme["error-01"]}>
            {qtyError ? qtyError : caratError}
          </VDBText>
        </ErrorContainer>
      ) : null}
      <CommentContainer>
        <Input
          inputLabel={t("bmh.input.comments.placeHolder.text")}
          onChangeText={(val) => onValueChange(BulkQuantityField.Comment, val)}
          value={comments}
          onBlur={onBlurUpdate}
        />
      </CommentContainer>
    </>
  );
};

const RenderStaticForm = (
  getLabel: any,
  quantity: string,
  totalCarat: string,
  typography: any,
  getEstimatedPrice: any,
  theme: any
) => {
  return (
    <>
      <DataContainer>
        <ValueParentContainer containerWidth={88}>
          <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
            {getLabel("carat", "Total Carat")}
          </VDBText>
          <Spacer size={SpaceType.Double} />
          <ValueContainer>
            <VDBText type={typography["Minor-M"]} color={theme["core-05"]}>
              {totalCarat}
            </VDBText>
          </ValueContainer>
        </ValueParentContainer>
        <Spacer size={SpaceType.Double} />
        <ValueParentContainer containerWidth={55}>
          <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
            {getLabel("quantity", "Quantity")}
          </VDBText>
          <Spacer size={SpaceType.Double} />
          <ValueContainer>
            <VDBText type={typography["Minor-M"]} color={theme["core-05"]}>
              {quantity}
            </VDBText>
          </ValueContainer>
        </ValueParentContainer>
        <Spacer size={SpaceType.Double} />
        <ValueParentContainer containerWidth={88}>
          <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
            {getLabel("price", "* Est. Price")}
          </VDBText>
          <Spacer size={SpaceType.Double} />
          <ValueContainer>
            <VDBText type={typography["Minor-M"]} color={theme["core-05"]}>
              {getEstimatedPrice}
            </VDBText>
          </ValueContainer>
        </ValueParentContainer>
      </DataContainer>
    </>
  );
};

const QuantityInput = React.memo(
  ({
    label,
    fieldName,
    quantity,
    totalCarat,
    onValueChange,
    onBlurChange,
    containerWidth,
    enableFourDecimalCarat,
  }: {
    label: string;
    fieldName: string;
    quantity: string;
    totalCarat: string;
    onValueChange: (fieldName: string, val: string) => void;
    onBlurChange?: (fieldName: string) => void;
    containerWidth: Number;
    enableFourDecimalCarat: boolean;
  }) => {
    const theme = useTheme();
    const handleBlur = (mFieldName: string, value: string) => {
      const numericValue = removeCommaFormatted(value);
      if (mFieldName === BulkQuantityField.TotalCarat) {
        if (!value.endsWith(" ct")) {
          if (
            numericValue === "" ||
            numericValue === "0" ||
            numericValue === undefined
          ) {
            onValueChange(mFieldName, "");
          } else {
            const formatted = `${formatCarat(numericValue)} ct`;

            onValueChange(mFieldName, formatted);
          }
        }
      }

      onBlurChange && onBlurChange(mFieldName);
    };

    const handleFocus = (xFieldName: string, value: string) => {
      if (
        xFieldName === BulkQuantityField.TotalCarat &&
        value.endsWith(" ct")
      ) {
        const cleanValue = removeCt(value);
        onValueChange(xFieldName, cleanValue);
      }
    };

    const formatCarat = (val: string) => {
      if (!val) return "";
      const num = Number(val);
      return isNaN(num)
        ? ""
        : num.toLocaleString("en-US", {
            minimumFractionDigits: enableFourDecimalCarat ? 4 : 3,
            maximumFractionDigits: enableFourDecimalCarat ? 4 : 3,
          });
    };

    return (
      <View
        style={{
          width:
            theme.isLargeScreen || theme.isTablet
              ? 96
              : (containerWidth as number) / 3,
        }}
      >
        <Input
          inputLabel={label}
          keyboardType="numeric"
          onBlur={() =>
            handleBlur(
              fieldName,
              fieldName === BulkQuantityField.Quantity ? quantity : totalCarat
            )
          }
          onFocus={() =>
            handleFocus(
              fieldName,
              fieldName === BulkQuantityField.Quantity ? quantity : totalCarat
            )
          }
          onChangeText={(val) => onValueChange(fieldName, val)}
          value={
            fieldName === BulkQuantityField.Quantity ? quantity : totalCarat
          }
        />
      </View>
    );
  }
);

const DataContainer = styled.View`
  flex-direction: row;
  justify-content: flex-end;
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-01"] as string};
  padding: 8px;
  border-bottom-left-radius: ${SpaceType.Basic}px;
  border-bottom-right-radius: ${SpaceType.Basic}px;
`;

const ParentContainer = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-01"] as string};
  border-bottom-left-radius: ${SpaceType.Basic}px;
  border-bottom-right-radius: ${SpaceType.Basic}px;
`;

const ErrorContainer = styled.View`
  align-items: end;
  padding: 4px 8px;
`;

const EstimatedPriceContainer = styled.View<any>`
  min-width: ${({ containerWidth }: { containerWidth: number }) =>
    `${containerWidth}px`};
  padding-end: 4px;
`;

const ValueParentContainer = styled.View<any>`
  width: ${({ containerWidth }: { containerWidth: number }) =>
    `${containerWidth}px`};
  align-items: flex-end;
  padding-end: 4px;
`;

const PriceContainer = styled.View``;

const ValueContainer = styled.View`
  align-items: flex-end;
`;

const CommentContainer = styled.View`
  border-top-width: 1px;
  border-top-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["core-03"] as string};
  border-bottom-left-radius: ${SpaceType.Basic}px;
  border-bottom-right-radius: ${SpaceType.Basic}px;
  padding: 8px;
`;

export default BulkQuantityEditor;
